import { BarChart } from "@/components/charts/bar-chart";
import { LineChart } from "@/components/charts/line-chart";
import { PieChart } from "@/components/charts/pie-chart";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DataTable, type DataTableColumn } from "@/components/ui/data-table";
import { Select } from "@/components/ui/select";
import { ApiError, api } from "@/lib/api";
import { cn, formatCurrency, formatHours } from "@/lib/utils";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { Clock } from "lucide-react";
import { z } from "zod";

/**
 * `/reports` — Analytics + Operations, the single accountant /
 * admin overview page. Non-accountants are bounced to `/my` (the
 * server also 403s them).
 *
 * Sections:
 *   - **Operations** — short-horizon company pulse: pending hours
 *     waiting for approval + the five issue / time / activity
 *     charts that used to live on the standalone `/dashboard`.
 *   - **Company health** — financial trend: revenue, tracked hours,
 *     hours mix, invoice-status mix.
 *   - **Project profitability** — margin ranking + per-project table.
 *   - **Employee profitability & utilisation** — margin ranking +
 *     utilisation + per-employee table.
 *
 * The month-range selector in the URL drives the financial queries
 * and defaults to the last six months; the Operations section is
 * fixed to its own native windows (last 7 / 14 / 30 days).
 */

const analyticsSearchSchema = z.object({
  from: z
    .string()
    .regex(/^\d{4}-\d{2}$/)
    .optional(),
  to: z
    .string()
    .regex(/^\d{4}-\d{2}$/)
    .optional(),
});

type AnalyticsSearch = z.infer<typeof analyticsSearchSchema>;

type MonthRow = {
  label: string;
  invoiced: number;
  revenue: number;
  cost: number;
  hours: number;
  billableHours: number;
  unbilledHours: number;
  nonBillableHours: number;
};

type ProjectRow = {
  id: string;
  name: string;
  revenue: number;
  cost: number;
  margin: number;
  marginPct: number;
  hours: number;
  billableHours: number;
};

type EmployeeRow = {
  id: string;
  name: string;
  revenue: number;
  cost: number;
  margin: number;
  marginPct: number;
  hours: number;
  billableHours: number;
  nonBillableHours: number;
  utilisation: number;
};

type AnalyticsData = {
  period: { from: string; to: string };
  totals: {
    invoiced: number;
    paidInvoices: number;
    revenue: number;
    cost: number;
    margin: number;
    marginPct: number;
    billableHours: number;
    unbilledHours: number;
    nonBillableHours: number;
    trackedHours: number;
  };
  monthly: MonthRow[];
  invoiceStatusMix: Array<{ label: string; value: number }>;
  hoursMix: Array<{ label: string; value: number }>;
  projectProfit: ProjectRow[];
  employeeProfit: EmployeeRow[];
};

/** Short-horizon counts + per-day series powering the Operations
 *  section. Source: `/api/dashboard` (kept distinct from /api/reports
 *  because its windows are different — last 7 / 14 days, not a
 *  user-pickable month range). */
type ChartPoint = { label: string; value: number; color?: string };
type OperationsData = {
  pendingHours: number;
  statusPieData: ChartPoint[];
  priorityBarData: ChartPoint[];
  weekChartData: ChartPoint[];
  topProjects: ChartPoint[];
  activityChartData: ChartPoint[];
};

type LoaderData = { analytics: AnalyticsData; operations: OperationsData };

export const Route = createFileRoute("/_app/reports")({
  validateSearch: analyticsSearchSchema,
  loaderDeps: ({ search }) => ({ from: search.from, to: search.to }),
  loader: async ({ deps }): Promise<LoaderData> => {
    const params = new URLSearchParams();
    if (deps.from) params.set("from", deps.from);
    if (deps.to) params.set("to", deps.to);
    const qs = params.toString();
    try {
      const [analytics, operations] = await Promise.all([
        api.get<AnalyticsData>(qs ? `/api/reports?${qs}` : "/api/reports"),
        api.get<OperationsData>("/api/dashboard"),
      ]);
      return { analytics, operations };
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) throw redirect({ to: "/my" });
      throw err;
    }
  },
  component: AnalyticsPage,
});

/** A list of the last 24 month keys, newest first, for the selectors. */
function monthOptions(): Array<{ value: string; label: string }> {
  const now = new Date();
  const out: Array<{ value: string; label: string }> = [];
  for (let i = 0; i < 24; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push({
      value: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`,
      label: d.toLocaleString("default", { month: "short", year: "numeric" }),
    });
  }
  return out;
}

function AnalyticsPage() {
  const { analytics: data, operations } = Route.useLoaderData();
  const navigate = Route.useNavigate();
  const months = monthOptions();

  const setRange = (next: Partial<AnalyticsSearch>) => {
    navigate({
      search: (prev) => ({ ...prev, ...next }),
    });
  };

  const revenueTrend = data.monthly.map((m) => ({ label: m.label, value: m.invoiced }));
  const trackedHoursTrend = data.monthly.map((m) => ({ label: m.label, value: m.hours }));
  const billedVsUnbilled = data.monthly.map((m) => ({
    label: m.label,
    value: m.billableHours,
  }));

  const projectMarginBar = data.projectProfit.slice(0, 12).map((p) => ({
    label: p.name,
    value: p.margin,
    color: p.margin >= 0 ? "hsl(var(--primary))" : "#ef4444",
  }));

  const revenueConcentration = data.projectProfit
    .filter((p) => p.revenue > 0)
    .slice(0, 8)
    .map((p) => ({ label: p.name, value: p.revenue }));

  const employeeRevenueBar = data.employeeProfit
    .filter((e) => e.revenue > 0 || e.cost > 0)
    .slice(0, 12)
    .map((e) => ({ label: e.name, value: e.margin }));

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold">Analytics</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Company health and the profitability of projects and people
          </p>
        </div>
        <div className="flex items-end gap-2">
          <MonthSelect
            label="From"
            value={data.period.from}
            options={months}
            onChange={(v) => setRange({ from: v })}
          />
          <MonthSelect
            label="To"
            value={data.period.to}
            options={months}
            onChange={(v) => setRange({ to: v })}
          />
        </div>
      </div>

      {/* ---- Operations (lifted from the retired /dashboard) ------------ */}
      <Section title="Operations">
        <div className="grid grid-cols-1 sm:max-w-xs">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Pending Hours
                </p>
                <Clock className="h-4 w-4 text-accent" />
              </div>
              <p className="font-heading text-2xl font-bold mt-2">
                {formatHours(operations.pendingHours)}
              </p>
            </CardContent>
          </Card>
        </div>

        <div className="grid gap-5 lg:grid-cols-2">
          <ChartCard title="Issues by Status">
            <BarChart
              data={operations.statusPieData}
              orientation="horizontal"
              showValues
              formatValue={(v) => String(v)}
            />
          </ChartCard>

          <ChartCard title="Issues by Priority">
            <BarChart
              data={operations.priorityBarData}
              showValues
              orientation="vertical"
              formatValue={(v) => String(v)}
            />
          </ChartCard>

          <ChartCard title="Time Logged Last 7 Days" subtitle="Hours per day">
            <LineChart data={operations.weekChartData} showDots fill />
          </ChartCard>

          <ChartCard title="Top Projects This Month">
            <BarChart
              data={operations.topProjects}
              orientation="horizontal"
              showValues
              formatValue={(v) => `${v.toFixed(1)}h`}
            />
            {operations.topProjects.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-4">
                No time entries this month.
              </p>
            )}
          </ChartCard>

          <Card className="lg:col-span-2">
            <CardContent className="p-5">
              <h3 className="font-heading text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                Activity — Last 14 Days
              </h3>
              <p className="text-xs text-muted-foreground mb-3">Journal entries per day</p>
              <LineChart
                data={operations.activityChartData}
                showDots
                fill
                color="hsl(var(--accent))"
              />
            </CardContent>
          </Card>
        </div>
      </Section>

      {/* ---- Headline numbers ------------------------------------------ */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard label="Invoiced" value={formatCurrency(data.totals.invoiced)} />
        <StatCard
          label="Revenue (billable time)"
          value={formatCurrency(data.totals.revenue)}
          valueClass="text-green-600"
        />
        <StatCard
          label="Cost (time × cost rate)"
          value={formatCurrency(data.totals.cost)}
          valueClass="text-red-600"
        />
        <StatCard
          label="Margin"
          value={`${formatCurrency(data.totals.margin)} · ${data.totals.marginPct.toFixed(0)}%`}
          valueClass={data.totals.margin >= 0 ? "text-green-600" : "text-red-600"}
        />
      </div>

      {/* ---- Company health -------------------------------------------- */}
      <Section title="Company health">
        <div className="grid gap-6 lg:grid-cols-2">
          <ChartCard
            title="Monthly revenue — invoiced amounts"
            subtitle={`${formatCurrency(data.totals.invoiced)} invoiced this period`}
          >
            <BarChart
              data={revenueTrend}
              showValues={false}
              orientation="vertical"
              formatValue={(v) => formatCurrency(v)}
            />
          </ChartCard>

          <ChartCard
            title="Tracked hours per month"
            subtitle={`${formatHours(data.totals.trackedHours)} tracked in total`}
          >
            <LineChart data={trackedHoursTrend} formatValue={(v) => `${v.toFixed(0)}h`} />
          </ChartCard>

          <ChartCard
            title="Billed vs unbilled vs non-billable hours"
            subtitle="Where logged time lands"
          >
            <div className="flex flex-wrap items-center gap-6">
              <PieChart
                data={data.hoursMix}
                size={140}
                donut
                showLegend
                formatValue={formatHours}
              />
              <div className="space-y-1 text-sm">
                <MixRow label="Billable" value={data.totals.billableHours} />
                <MixRow label="Unbilled" value={data.totals.unbilledHours} />
                <MixRow label="Non-billable" value={data.totals.nonBillableHours} />
              </div>
            </div>
          </ChartCard>

          <ChartCard
            title="Invoice status mix"
            subtitle={`${formatCurrency(data.totals.paidInvoices)} paid`}
          >
            <PieChart
              data={data.invoiceStatusMix}
              size={140}
              donut
              showLegend
              formatValue={(v) => formatCurrency(v)}
            />
          </ChartCard>
        </div>

        <ChartCard title="Billable hours trend" subtitle="Monthly billable time">
          <BarChart
            data={billedVsUnbilled}
            showValues={false}
            orientation="vertical"
            formatValue={(v) => `${v.toFixed(0)}h`}
          />
        </ChartCard>
      </Section>

      {/* ---- Project profitability ------------------------------------- */}
      <Section title="Project profitability">
        <div className="grid gap-6 lg:grid-cols-2">
          <ChartCard title="Project margin ranking" subtitle="Revenue minus cost, top projects">
            <BarChart
              data={projectMarginBar}
              orientation="horizontal"
              showValues
              formatValue={(v) => formatCurrency(v)}
            />
          </ChartCard>

          <ChartCard title="Revenue concentration" subtitle="Share of billed revenue by project">
            <PieChart
              data={revenueConcentration}
              size={150}
              donut
              showLegend
              formatValue={(v) => formatCurrency(v)}
            />
          </ChartCard>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              Per-project breakdown
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.projectProfit.length === 0 ? (
              <EmptyNote />
            ) : (
              <ProjectBreakdownTable rows={data.projectProfit} />
            )}
          </CardContent>
        </Card>
      </Section>

      {/* ---- Employee profitability ------------------------------------ */}
      <Section title="Employee profitability &amp; utilisation">
        <div className="grid gap-6 lg:grid-cols-2">
          <ChartCard title="Employee margin ranking" subtitle="Revenue generated minus cost">
            <BarChart
              data={employeeRevenueBar}
              orientation="horizontal"
              showValues
              formatValue={(v) => formatCurrency(v)}
            />
          </ChartCard>

          <ChartCard title="Utilisation" subtitle="Billable share of logged hours">
            <BarChart
              data={data.employeeProfit
                .filter((e) => e.hours > 0)
                .slice(0, 12)
                .map((e) => ({ label: e.name, value: e.utilisation }))}
              orientation="horizontal"
              showValues
              formatValue={(v) => `${v.toFixed(0)}%`}
            />
          </ChartCard>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              Per-employee breakdown
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.employeeProfit.length === 0 ? (
              <EmptyNote />
            ) : (
              <EmployeeBreakdownTable rows={data.employeeProfit} />
            )}
          </CardContent>
        </Card>
      </Section>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-6">
      <h2 className="font-heading text-lg font-semibold">{title}</h2>
      {children}
    </section>
  );
}

function ChartCard({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <Card>
      <CardContent className="p-5">
        <h3 className="font-heading text-sm font-semibold text-foreground">{title}</h3>
        {subtitle && <p className="text-xs text-muted-foreground mt-0.5 mb-4">{subtitle}</p>}
        {!subtitle && <div className="mb-4" />}
        {children}
      </CardContent>
    </Card>
  );
}

function StatCard({
  label,
  value,
  valueClass,
}: {
  label: string;
  value: string;
  valueClass?: string;
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{label}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className={`text-xl font-bold ${valueClass ?? ""}`}>{value}</div>
      </CardContent>
    </Card>
  );
}

function MixRow({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex items-center justify-between gap-6">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium tabular-nums">{formatHours(value)}</span>
    </div>
  );
}

function MonthSelect({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: Array<{ value: string; label: string }>;
  onChange: (v: string) => void;
}) {
  return (
    <span className="flex flex-col gap-1 text-xs text-muted-foreground">
      {label}
      <Select aria-label={label} value={value} onChange={onChange} options={options} />
    </span>
  );
}

function EmptyNote() {
  return <p className="text-sm text-muted-foreground text-center py-6">No data for this period.</p>;
}

const projectColumns: Array<DataTableColumn<ProjectRow>> = [
  {
    key: "name",
    header: "Project",
    sortAccessor: (p) => p.name.toLowerCase(),
    cellClassName: "font-medium",
    cell: (p) => p.name,
  },
  {
    key: "hours",
    header: "Hours",
    sortAccessor: (p) => p.hours,
    headClassName: "text-right",
    cellClassName: "text-right tabular-nums",
    cell: (p) => formatHours(p.hours),
  },
  {
    key: "revenue",
    header: "Revenue",
    sortAccessor: (p) => p.revenue,
    headClassName: "text-right",
    cellClassName: "text-right tabular-nums",
    cell: (p) => formatCurrency(p.revenue),
  },
  {
    key: "cost",
    header: "Cost",
    sortAccessor: (p) => p.cost,
    headClassName: "text-right",
    cellClassName: "text-right tabular-nums",
    cell: (p) => formatCurrency(p.cost),
  },
  {
    key: "margin",
    header: "Margin",
    sortAccessor: (p) => p.margin,
    headClassName: "text-right",
    cellClassName: (p) =>
      cn("text-right tabular-nums font-medium", p.margin >= 0 ? "text-green-600" : "text-red-600"),
    cell: (p) => formatCurrency(p.margin),
  },
  {
    key: "marginPct",
    header: "Margin %",
    sortAccessor: (p) => p.marginPct,
    headClassName: "text-right",
    cellClassName: "text-right tabular-nums text-muted-foreground",
    cell: (p) => `${p.marginPct.toFixed(0)}%`,
  },
];

function ProjectBreakdownTable({ rows }: { rows: ProjectRow[] }) {
  return <DataTable columns={projectColumns} rows={rows} rowKey={(p) => p.id} bare />;
}

const employeeColumns: Array<DataTableColumn<EmployeeRow>> = [
  {
    key: "name",
    header: "Employee",
    sortAccessor: (e) => e.name.toLowerCase(),
    cellClassName: "font-medium",
    cell: (e) => e.name,
  },
  {
    key: "hours",
    header: "Hours",
    sortAccessor: (e) => e.hours,
    headClassName: "text-right",
    cellClassName: "text-right tabular-nums",
    cell: (e) => formatHours(e.hours),
  },
  {
    key: "billableHours",
    header: "Billable",
    sortAccessor: (e) => e.billableHours,
    headClassName: "text-right",
    cellClassName: "text-right tabular-nums text-muted-foreground",
    cell: (e) => formatHours(e.billableHours),
  },
  {
    key: "revenue",
    header: "Revenue",
    sortAccessor: (e) => e.revenue,
    headClassName: "text-right",
    cellClassName: "text-right tabular-nums",
    cell: (e) => formatCurrency(e.revenue),
  },
  {
    key: "cost",
    header: "Cost",
    sortAccessor: (e) => e.cost,
    headClassName: "text-right",
    cellClassName: "text-right tabular-nums",
    cell: (e) => formatCurrency(e.cost),
  },
  {
    key: "margin",
    header: "Margin",
    sortAccessor: (e) => e.margin,
    headClassName: "text-right",
    cellClassName: (e) =>
      cn("text-right tabular-nums font-medium", e.margin >= 0 ? "text-green-600" : "text-red-600"),
    cell: (e) => formatCurrency(e.margin),
  },
  {
    key: "utilisation",
    header: "Utilisation",
    sortAccessor: (e) => e.utilisation,
    headClassName: "text-right",
    cellClassName: "text-right tabular-nums text-muted-foreground",
    cell: (e) => `${e.utilisation.toFixed(0)}%`,
  },
];

function EmployeeBreakdownTable({ rows }: { rows: EmployeeRow[] }) {
  return <DataTable columns={employeeColumns} rows={rows} rowKey={(e) => e.id} bare />;
}
